import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MOCKUP_ASSETS } from '../utils/assets';

interface Review {
  title: string;
  author: string;
  time: string;
  text: string;
}

interface MockupData {
  id: number;
  image: string;
  leftReview: Review;
  rightReview: Review;
}

// Konstanten für bessere Performance
const ROTATION_INTERVAL = 7500; // 7.5 Sekunden
const TRANSITION_DURATION = 700; // 700ms für langsamere, flüssigere Animation

const useMockupRotation = (isMockupVisible: boolean = false, isInViewport: boolean = false) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(0); // Für die angezeigten Reviews
  const [isTransitioning, setIsTransitioning] = useState(false);
  

  // Refs für Timer-Management
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const transitionTimeoutRef = useRef<number | null>(null);
  
  // Memoized Mockup-Daten für bessere Performance
  const mockupData: MockupData[] = useMemo(() => [
    {
      id: 1,
      image: MOCKUP_ASSETS.mockup1,
      leftReview: {
        title: 'Perfekter Produktivitätshelfer!',
        author: 'furata',
        time: ' vor 2 Wochen • furata ',
        text: 'Die feela App hat meinen Arbeitsalltag komplett verändert. Der Pomodoro-Timer hilft mir, fokussiert zu bleiben, und die Benachrichtigungen kommen genau zur richtigen Zeit. So vergesse ich nie mehr meine Pausen und bleibe produktiv. Die Benutzeroberfläche ist intuitiv und ansprechend gestaltet.'
      },
      rightReview: {
        title: 'Endlich eine App, die wirklich hilft',
        author: 'mrseo',
        time: ' vor 1 Monat • mrseo ',
        text: 'Wir setzen die App in unserem Team ein und sind begeistert. Die anpassbaren Pomodoro-Einstellungen passen sich perfekt an unsere Arbeitsabläufe an, und die zuverlässigen Benachrichtigungen sorgen für einen strukturierten Arbeitsrhythmus.'
      }
    },
    {
      id: 2,
      image: MOCKUP_ASSETS.mockup2,
      leftReview: {
        title: 'Ein MUSS für Partygänger',
        author: 'business42',
        time: ' vor 3 Wochen • business42 ',
        text: 'Als Partygänger ist diese App für mich unerlässlich! Die Echtzeit-Updates zu Veranstaltungen und die genauen Infos zu Getränkeoptionen haben mir schon viele spontane Clubnächte gerettet.'
      },
      rightReview: {
        title: 'Praktische App für Berlin',
        author: 'techfounder',
        time: ' vor 2 Wochen • techfounder ',
        text: 'Nach langem Suchen endlich eine App, die wirklich viele Events in Berlin zeigt... die Karte mit Live-Updates hat meine Abendplanung revolutioniert.'
      }
    },
    {
      id: 3,
      image: MOCKUP_ASSETS.mockup3,
      leftReview: {
        title: 'Super',
        author: 'Peter4506',
        time: ' vor 1 Jahr • Peter4506 ',
        text: 'Mit Abstand die beste Übersetzungs-App! Auch wenn nicht immer alles perfekt ist, liefert sie deutlich bessere Ergebnisse als Google Übersetzer & Co. – eine echte Hilfe im Schulalltag!'
      },
      rightReview: {
        title: 'Sehr gut',
        author: 'Mortis',
        time: ' vor 2 Wochen • Mortis ',
        text: 'Ich bin wirklich dankbar für diese App – sie hilft mir enorm bei meinen Hausaufgaben und liefert großartige Übersetzungen!'
      }
    },
    {
      id: 4,
      image: MOCKUP_ASSETS.mockup4,
      leftReview: {
        title: 'Wirklich gute App',
        author: 'Fachwirteshop',
        time: ' vor 3 Monaten • Fachwirteshop ',
        text: 'Die Lernkarten habe ich schon vor Jahren genutzt, als ich selbst den Fachwirt gemacht habe. Heute nutze ich sie gerne im Unterricht, für ein kleines Frage und Antwort Quiz vor Prüfungen. Fachlich sind die wirklich richtig gut.'
      },
      rightReview: {
        title: 'Klare Weiterempfehlung',
        author: 'NatNatNani',
        time: ' vor 2 Wochen • NatNatNani ',
        text: 'Ich habe mir die App zugelegt um im Urlaub und unterwegs lernen zu können. Nachdem der Kauf abgeschlossen war und es ein kleines Problem gab, wurde mir sofort geholfen. Ich kann es nur weiterempfehlen! Sehr netter Service sowie guter Aufbau zu lernen, da die jeweiligen Bereiche untergliedert sind (Teil 1, Teil 2). :-)'
      }
    }
  ], []);

  // Cleanup-Funktion für alle Timer
  const clearAllTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
  }, []);

  // Vereinfachte Transition-Logik - direkter Ansatz
  const triggerTransition = useCallback(() => {
    if (isTransitioning) {
      return;
    }
    
    setIsTransitioning(true);
    

    const delayMultiplier =  0.6;
    const actualDelay = TRANSITION_DURATION * delayMultiplier;
    
    console.log(`Using delay: ${actualDelay}ms (${delayMultiplier * 100}%)`);
    
    setTimeout(() => {
      setActiveIndex(prev => {
        const newIndex = (prev + 1) % mockupData.length;
        return newIndex;
      });
      setDisplayIndex(prev => {
        const newIndex = (prev + 1) % mockupData.length;
        return newIndex;
      });
    }, actualDelay);
    
    // Transition-State nach kompletter Delay zurücksetzen
    setTimeout(() => {
      setIsTransitioning(false);
    }, TRANSITION_DURATION);
  }, [isTransitioning, mockupData.length]);

  // Vereinfachte Auto-Rotation - nur starten wenn Mockups sichtbar UND im Viewport sind
  const startAutoRotation = useCallback(() => {
    if (!isMockupVisible || !isInViewport) {
      return;
    }
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    intervalRef.current = setInterval(() => {
      triggerTransition();
    }, ROTATION_INTERVAL);
  }, [triggerTransition, isMockupVisible, isInViewport]);

  // Haupteffekt für Auto-Rotation - nur wenn Mockups sichtbar UND im Viewport sind
  useEffect(() => {
    if (isMockupVisible && isInViewport) {
      startAutoRotation();
    } else {
      clearAllTimers();
    }
    
    return () => {
      clearAllTimers();
    };
  }, [isMockupVisible, isInViewport, startAutoRotation, clearAllTimers]);

  // Memoized current mockup für bessere Performance
  const currentMockup = useMemo(() => mockupData[activeIndex], [mockupData, activeIndex]);
  
  // Memoized current reviews für parallelen Wechsel
  const currentReviews = useMemo(() => mockupData[displayIndex], [mockupData, displayIndex]);

  return {
    currentMockup,
    currentReviews,
    isTransitioning,
    mockupData,
  };
};

export default useMockupRotation;
